import { Variant, Product } from "@/types/types";
import { getDescription } from "@/utils/product/getDescription";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant";

type StructuredData = {
  "@type": string;
  "@context": string;
  offers: {
    "@type": string;
    seller: {
      "@type": string;
      name: string;
    };
    price: string;
    priceCurrency: string;
    priceSpecification: PriceSpecification;
    availability: string;
    url: string;
  };
  image: string;
  color: string | null | undefined;
  productId: string;
  description: string;
  mpn: string | undefined;
  url: string;
  name: string;
  sku: string | undefined;
  brand: {
    "@type": string;
    name: string | undefined;
    logo: string | undefined;
  };
  aggregateRating?: {
    "@type": "AggregateRating";
    reviewCount: number;
    ratingValue: number;
  };
};

export type PriceSpecification = {
  price: string;
  priceCurrency: string;
  minPrice?: string;
  maxPrice?: string;
};

export const getStructuredData = (
  product: Product,
  selectedVariant: Variant,
  variantIsSelected: boolean,
): StructuredData => {
  const {
    __typename: productType,
    title,
    canonicalURL,
    aliases: { pimProductId: productId },
    reviewSummary,
  } = product;
  const description = getDescription(product);
  const canonicalPath = `https://www.johnlewis.com${canonicalURL}`;

  const {
    images: { primary: primaryImage },
  } = selectedVariant;

  let color: string | null | undefined = undefined;
  const { differentiators = {} } = selectedVariant;
  if (differentiators !== null) {
    const { colour } = differentiators;
    color = colour;
  }

  let logo: string | undefined = undefined;
  if (
    productType === "RetiredSimpleProduct" ||
    productType === "RetiredCompositeBundle"
  ) {
    if (product.brand.logo !== undefined) {
      logo = `https:${product.brand.logo}`;
    }
  }

  let mpn: string | undefined = undefined;
  let sku: string | undefined = undefined;
  let brandName: string | undefined = undefined;
  if (
    productType === "RetiredSimpleProduct" ||
    productType === "RetiredCompositeBundle"
  ) {
    mpn = selectedVariant.aliases.skuId;
    sku = selectedVariant.aliases.skuId;
    brandName = product.brand.name;
  } else if (
    productType === "RetiredStaticBundle" &&
    selectedVariant.components
  ) {
    mpn = selectedVariant.components[0]?.variant.aliases.skuId;
    sku = selectedVariant.components[0]?.variant.aliases.skuId;
  }

  let availability = "Discontinued";
  if (selectedVariant.availability?.availableToOrder) {
    availability = "InStock";
  } else if (selectedVariant.availability?.unavailableReason === "outOfStock") {
    availability = "OutOfStock";
  } else if (selectedVariant.availability?.preOrder) {
    availability = "PreOrder";
  } else if (
    selectedVariant.availability?.unavailableReason === "inStoreOnly"
  ) {
    availability = "InStoreOnly";
  }

  const priceSpecification: PriceSpecification = {
    price: selectedVariant?.price?.value || "0",
    priceCurrency: "GBP",
    minPrice: undefined,
    maxPrice: undefined,
  };

  const variantGroup = getColourGroupForVariant(
    product.variantGroups,
    selectedVariant.id,
  );
  const { min, max } = variantGroup?.variantPriceRange?.display || {
    min: undefined,
    max: undefined,
  };
  const productHasPriceRange: boolean = !variantIsSelected && min !== max;

  if (productHasPriceRange && !!min && !!max) {
    const minPrice = min.substring(1); // Remove the leading currency symbol
    const maxPrice = max.substring(1);
    priceSpecification.price = minPrice;
    priceSpecification.minPrice = minPrice;
    priceSpecification.maxPrice = maxPrice;
  }

  const { price, priceCurrency, minPrice, maxPrice } = priceSpecification;

  const structuredData: StructuredData = {
    "@type": "Product",
    "@context": "https://schema.org/",
    offers: {
      "@type": "Offer",
      seller: {
        "@type": "Organization",
        name: "John Lewis & Partners",
      },
      price,
      priceCurrency,
      priceSpecification: {
        price,
        priceCurrency,
      },
      availability: availability,
      url: canonicalPath,
    },
    image: `https:${primaryImage}`,
    color,
    productId: productId,
    description: description,
    mpn: mpn,
    url: canonicalPath,
    name: title,
    sku: sku,
    brand: {
      "@type": "Brand",
      name: brandName,
      logo,
    },
  };

  if (reviewSummary && reviewSummary.numberOfReviews > 0) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      reviewCount: reviewSummary.numberOfReviews,
      ratingValue: reviewSummary.averageRating,
    };
  }

  if (minPrice && maxPrice) {
    structuredData.offers.priceSpecification.minPrice = minPrice;
    structuredData.offers.priceSpecification.maxPrice = maxPrice;
  }

  return structuredData;
};
